import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { transfers, transferDestinations } from '@/mocks/transfers';

export default function Transfers() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language === 'de';

  const [selectedTransfer, setSelectedTransfer] = useState(transfers[0]);
  const [formSent, setFormSent] = useState(false);

  const [bookingForm, setBookingForm] = useState({
    route: '',
    date: '',
    passengers: 1,
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  const [submitUrl, setSubmitUrl] = useState('');

  const getCurrencySymbol = () => {
    return (window as any).__CURRENCY__?.symbol || '€';
  };

  const getConvertedPrice = (price: number) => {
    const rate = (window as any).__CURRENCY__?.rate || 1;
    return Math.round(price * rate);
  };

  const handleTransferChange = (transfer: typeof transfers[number]) => {
    setSelectedTransfer(transfer);

    setBookingForm({
      ...bookingForm,
      route: '',
      passengers: 1,
    });

    setFormSent(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bookingForm.route) {
      return;
    }

    let url = submitUrl;

    if (!url) {
      try {
        const formName = encodeURIComponent(
          `Yallabina Transfer: ${selectedTransfer.title_de}`
        );

        const response = await fetch(
          `https://readdy.ai/api/form/submit?name=${formName}`
        );

        const data = await response.json();

        url = data.submitAddr;
        setSubmitUrl(url);
      } catch {
        setFormSent(true);
        return;
      }
    }

    const formData = new URLSearchParams();

    formData.append(
      'transfer',
      isDe ? selectedTransfer.title_de : selectedTransfer.title_en
    );

    formData.append('route', bookingForm.route);
    formData.append('date', bookingForm.date);
    formData.append('passengers', String(bookingForm.passengers));
    formData.append('name', bookingForm.name);
    formData.append('email', bookingForm.email);
    formData.append('phone', bookingForm.phone);
    formData.append('notes', bookingForm.notes);

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
    } catch {
      // Ignore submission network errors
    }

    setFormSent(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 pt-28 md:pt-32">

        {/* ==================== HERO ==================== */}
        <section className="bg-primary-900 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">
              {t('transfers_title')}
            </h1>

            <p className="text-background-300 text-sm md:text-base max-w-2xl mx-auto">
              {t('transfers_subtitle')}
            </p>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20 py-10">

          {/* ==================== TRANSFER TYPE TABS ==================== */}
          <div className="flex flex-wrap gap-2 mb-10">
            {transfers.map((transfer) => (
              <button
                key={transfer.id}
                type="button"
                onClick={() => handleTransferChange(transfer)}
                className={`px-4 py-2 rounded-sm text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                  selectedTransfer.id === transfer.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i
                  className={`${
                    transfer.type === 'airport'
                      ? 'ri-flight-takeoff-line'
                      : transfer.type === 'limousine'
                        ? 'ri-vip-crown-line'
                        : transfer.type === 'minibus'
                          ? 'ri-bus-line'
                          : 'ri-hotel-line'
                  } mr-1.5`}
                />

                {isDe ? transfer.title_de : transfer.title_en}
              </button>
            ))}
          </div>

          <div className="flex flex-col lg:flex-row gap-8">

            {/* ==================== ROUTES + PRICING ==================== */}
            <div className="flex-1">

              <div className="mb-6">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-1">
                  {isDe
                    ? selectedTransfer.title_de
                    : selectedTransfer.title_en}
                </h2>

                <p className="text-sm text-foreground-500">
                  {isDe
                    ? selectedTransfer.description_de
                    : selectedTransfer.description_en}
                </p>
              </div>

              {/* Vehicle */}
              <div className="flex items-center gap-4 mb-6 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-car-line text-accent-500" />

                  {isDe
                    ? selectedTransfer.vehicle_de
                    : selectedTransfer.vehicle_en}
                </span>
              </div>

              {/* ==================== PRICE TABLE ==================== */}
              <div className="bg-background-50 border border-background-200 rounded-sm overflow-hidden mb-8">

                {/* Header */}
                <div className="grid grid-cols-4 gap-2 px-4 py-3 bg-primary-800 text-background-50 text-xs font-semibold uppercase tracking-wide">
                  <span>{t('transfers_from')}</span>
                  <span>{t('transfers_to')}</span>
                  <span>{t('transfers_duration')}</span>
                  <span>{t('transfers_price')}</span>
                </div>

                {/* Routes */}
{selectedTransfer.routes.map((route, index) => {
  const routeFrom = isDe ? route.from_de : route.from_en;
  const routeTo = isDe ? route.to_de : route.to_en;

  const routeValue = `${routeFrom} → ${routeTo}`;
  const isSelected = bookingForm.route === routeValue;

  return (
    <button
      key={index}
      type="button"
      onClick={() =>
        setBookingForm({
          ...bookingForm,
          route: routeValue,
        })
      }
      className={`grid grid-cols-4 gap-2 px-4 py-3 text-xs border-b border-background-200 w-full text-left cursor-pointer transition-colors ${
        isSelected
          ? 'bg-accent-50 border-l-2 border-l-accent-500'
          : 'hover:bg-background-50'
      }`}
    >
      <span className="text-foreground-700 font-medium">
        {routeFrom}
      </span>

      <span className="text-foreground-700 font-medium">
        {routeTo}
      </span>

      <span className="text-foreground-500">
        {route.duration}
      </span>

      <span className="text-accent-600 font-bold">
        {getCurrencySymbol()}
        {getConvertedPrice(route.price)}
      </span>
    </button>
  );
})}
              </div>

              {/* ==================== DESTINATIONS ==================== */}
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                {t('transfers_destinations')}
              </h3>

              <div className="flex flex-wrap gap-2">
                {transferDestinations.map((destination) => (
                  <span
                    key={destination}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-sm border border-background-200"
                  >
                    {destination}
                  </span>
                ))}
              </div>
            </div>

            {/* ==================== BOOKING FORM ==================== */}
            <div className="w-full lg:w-[380px] flex-shrink-0">

              <div className="lg:sticky lg:top-28 bg-background-50 border border-background-200 rounded-sm p-6">

                {formSent ? (

                  /* ==================== SUCCESS ==================== */
                  <div className="text-center py-8">

                    <i className="ri-checkbox-circle-fill text-accent-500 text-5xl mb-4 block" />

                    <h3 className="font-heading text-xl font-bold text-foreground-800 mb-2">
                      {isDe
                        ? 'Anfrage gesendet!'
                        : 'Request sent successfully!'}
                    </h3>

                    <p className="text-sm text-foreground-600">
                      {isDe
                        ? 'Wir melden uns innerhalb von 24 Stunden.'
                        : 'We will contact you within 24 hours.'}
                    </p>

                  </div>

                ) : (

                  /* ==================== FORM ==================== */
                  <form
                    data-readdy-form=""
                    onSubmit={handleSubmit}
                  >

                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                      {t('transfers_book')}
                    </h3>

                    <div className="space-y-3">

                      {/* Route */}
                      <input
                        type="text"
                        name="route"
                        value={bookingForm.route}
                        readOnly
                        required
                        placeholder={
                          isDe
                            ? 'Route auswählen (oben klicken)'
                            : 'Select a route above'
                        }
                        onClick={() => {
                          window.scrollTo({
                            top: 250,
                            behavior: 'smooth',
                          });
                        }}
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-sm text-sm text-foreground-800 cursor-pointer"
                      />

                      {/* Date */}
                      <input
                        type="date"
                        name="date"
                        value={bookingForm.date}
                        onChange={(event) =>
                          setBookingForm({
                            ...bookingForm,
                            date: event.target.value,
                          })
                        }
                        required
                        className="w-full px-3 py-2.5 border border-background-300 rounded-sm text-sm"
                      />

                      {/* Passengers */}
                      {/* Passengers */}
<div>
  <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
    {isDe ? 'Personen' : 'Passengers'}
  </label>

  <div className="flex items-center w-full border border-background-300 rounded-sm">

    {/* Minus */}
    <button
      type="button"
      onClick={() =>
        setBookingForm({
          ...bookingForm,
          passengers: Math.max(1, bookingForm.passengers - 1),
        })
      }
      disabled={bookingForm.passengers <= 1}
      className="shrink-0 w-12 h-12 flex items-center justify-center text-xl font-bold text-foreground-700 hover:bg-background-100 active:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed"
      aria-label={isDe ? 'Personen reduzieren' : 'Decrease passengers'}
    >
      −
    </button>

    {/* Number */}
    <input
      type="number"
      name="passengers"
      min={1}
      max={selectedTransfer.max_passengers}
      value={bookingForm.passengers}
      onChange={(event) => {
        const value = parseInt(event.target.value) || 1;

        setBookingForm({
          ...bookingForm,
          passengers: Math.min(
            selectedTransfer.max_passengers,
            Math.max(1, value)
          ),
        });
      }}
      required
      className="flex-1 min-w-0 h-12 text-center border-0 focus:outline-none focus:ring-0 appearance-none"
    />

    {/* Plus */}
    <button
      type="button"
      onClick={() =>
        setBookingForm({
          ...bookingForm,
          passengers: Math.min(
            selectedTransfer.max_passengers,
            bookingForm.passengers + 1
          ),
        })
      }
      disabled={
        bookingForm.passengers >= selectedTransfer.max_passengers
      }
      className="shrink-0 w-12 h-12 flex items-center justify-center text-xl font-bold text-foreground-700 hover:bg-background-100 active:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed"
      aria-label={isDe ? 'Personen erhöhen' : 'Increase passengers'}
    >
      +
    </button>

  </div>
</div>

                      {/* Email */}
                      <input
                        type="email"
                        name="email"
                        value={bookingForm.email}
                        onChange={(event) =>
                          setBookingForm({
                            ...bookingForm,
                            email: event.target.value,
                          })
                        }
                        required
                        placeholder={t('cart_email')}
                        className="w-full px-3 py-2.5 border border-background-300 rounded-sm text-sm"
                      />

                      {/* Phone */}
                      <input
                        type="tel"
                        name="phone"
                        value={bookingForm.phone}
                        onChange={(event) =>
                          setBookingForm({
                            ...bookingForm,
                            phone: event.target.value,
                          })
                        }
                        required
                        placeholder={t('cart_phone')}
                        className="w-full px-3 py-2.5 border border-background-300 rounded-sm text-sm"
                      />

                      {/* Notes */}
                      <textarea
                        name="notes"
                        value={bookingForm.notes}
                        onChange={(event) =>
                          setBookingForm({
                            ...bookingForm,
                            notes: event.target.value,
                          })
                        }
                        placeholder={t('tour_special_requests')}
                        maxLength={500}
                        rows={2}
                        className="w-full px-3 py-2.5 border border-background-300 rounded-sm text-sm resize-none"
                      />

                      {/* Submit */}
                      <button
                        type="submit"
                        className="w-full py-3.5 bg-accent-500 text-primary-950 text-sm font-bold rounded-sm hover:bg-accent-400 cursor-pointer uppercase tracking-wide whitespace-nowrap"
                      >
                        {t('transfers_book')}
                      </button>

                    </div>
                  </form>
                )}

              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}